import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Receipt, X, Loader2, Truck, Activity } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { ModalFaturar, ModalProgressoFaturamento } from './CargasDistribuicao'

/**
 * Painel "Faturamento pendente": lista as cargas de distribuição em aberto que
 * faltam faturar e dispara o faturamento em lote (reusa o ModalFaturar). Aberto
 * pelo botão 🧾 Faturamento no cabeçalho da tela de Vendas.
 */
export function ModalFaturamentoPendente({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const { data: cargas = [], isLoading } = useQuery<any[]>({
    queryKey: ['cargas-pendentes-faturar'],
    queryFn: async () => (await api.get('/vendas/cargas/', { params: { status: 'aberta', tipo: 'distribuicao' } })).data,
  })
  const [faturarCarga, setFaturarCarga] = useState<any>(null)
  const [monitorCargaId, setMonitorCargaId] = useState<number | null>(null)
  const faturar = useMutation({
    mutationFn: async ({ id, sem_nota_ids, motorista_id, veiculo_id, conta_bancaria_id }: { id: number; sem_nota_ids: number[]; motorista_id: number | null; veiculo_id: number | null; conta_bancaria_id?: number | null }) =>
      (await api.post(`/vendas/cargas/${id}/faturar`, { sem_nota_ids, motorista_id, veiculo_id, conta_bancaria_id }, { timeout: 60000 })).data,
    onSuccess: (d: any, vars: any) => {
      toast.success(`Faturamento iniciado — acompanhe o progresso (${d.total} pedidos)`, { duration: 4000 })
      setFaturarCarga(null)
      setMonitorCargaId(vars.id)
      qc.invalidateQueries({ queryKey: ['cargas-pendentes-faturar'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao faturar carga'),
  })

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[88vh] overflow-auto">
          <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white z-10">
            <h3 className="font-semibold flex items-center gap-2"><Receipt size={16} /> Faturamento pendente</h3>
            <button onClick={onClose}><X size={18} /></button>
          </div>
          <div className="p-5 space-y-3">
            <p className="text-xs text-ink-500">Cargas de distribuição <strong>em aberto</strong> que faltam faturar. Clique em Faturar pra emitir em lote (com/sem nota).</p>
            {isLoading ? (
              <div className="text-center text-ink-400 py-8"><Loader2 className="animate-spin inline mr-2" /> Carregando…</div>
            ) : cargas.length === 0 ? (
              <div className="text-center text-ink-500 py-8">🎉 Nenhuma carga pendente de faturamento.</div>
            ) : (
              <div className="border rounded divide-y">
                {cargas.map((c) => (
                  <div key={c.id} className="flex items-center justify-between px-3 py-2.5 text-sm">
                    <div className="flex items-center gap-2">
                      <Truck size={15} className="text-ink-400" />
                      <div>
                        <div className="font-medium">#{c.numero}{c.nome ? ` · ${c.nome}` : ''}</div>
                        <div className="text-xs text-ink-500">{c.total_pedidos} pedido(s){c.valor_total ? ` · ${formatMoney(c.valor_total)}` : ''}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setMonitorCargaId(c.id)} title="Acompanhar faturamento"
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium">
                        <Activity size={13} /> Monitor
                      </button>
                      <button onClick={() => setFaturarCarga(c)} disabled={faturar.isPending}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50">
                        <Receipt size={13} /> Faturar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {faturarCarga && <ModalFaturar carga={faturarCarga} faturando={faturar.isPending}
        onClose={() => setFaturarCarga(null)}
        onFaturar={(semNotaIds: number[], motoristaId: number | null, veiculoId: number | null, bancoBoletoId: number | null) =>
          faturar.mutate({ id: faturarCarga.id, sem_nota_ids: semNotaIds, motorista_id: motoristaId, veiculo_id: veiculoId, conta_bancaria_id: bancoBoletoId })} />}
      {monitorCargaId != null && <ModalProgressoFaturamento cargaId={monitorCargaId}
        onClose={() => { setMonitorCargaId(null); qc.invalidateQueries({ queryKey: ['cargas-pendentes-faturar'] }) }} />}
    </>
  )
}
